"use client"

import { useCallback, useEffect, useState } from "react"

const STORAGE_KEY = "ca:articles-read:v1"
const CHANGE_EVENT = "ca:articles-read-changed"

function readAll(): Set<string> {
  if (typeof window === "undefined") return new Set()
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === "string"))
  } catch {
    return new Set()
  }
}

function writeAll(set: Set<string>) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)))
    queueMicrotask(() => window.dispatchEvent(new Event(CHANGE_EVENT)))
  } catch {
    // best-effort UX, not load-bearing
  }
}

export function markRead(slug: string) {
  const set = readAll()
  if (set.has(slug)) return
  set.add(slug)
  writeAll(set)
}

export function unmarkRead(slug: string) {
  const set = readAll()
  if (!set.has(slug)) return
  set.delete(slug)
  writeAll(set)
}

export function toggleRead(slug: string) {
  const set = readAll()
  if (set.has(slug)) set.delete(slug)
  else set.add(slug)
  writeAll(set)
}

/**
 * Reactive hook returning whether a given article is marked read,
 * plus a toggle function. Stays in sync with other tabs and other
 * components in the same tab via the custom change event.
 */
export function useArticleRead(slug: string) {
  const [read, setRead] = useState(false)

  useEffect(() => {
    function refresh() {
      setRead(readAll().has(slug))
    }
    refresh()
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) refresh()
    }
    window.addEventListener("storage", onStorage)
    window.addEventListener(CHANGE_EVENT, refresh)
    return () => {
      window.removeEventListener("storage", onStorage)
      window.removeEventListener(CHANGE_EVENT, refresh)
    }
  }, [slug])

  const toggle = useCallback(() => {
    toggleRead(slug)
  }, [slug])

  return { read, toggle, mark: () => markRead(slug), unmark: () => unmarkRead(slug) }
}

/**
 * Subscribe to the full set of read article slugs. Useful on grid pages
 * (articles index, category, homepage) that need to dim every read card.
 */
export function useReadSet() {
  const [set, setSet] = useState<Set<string>>(new Set())

  useEffect(() => {
    function refresh() {
      setSet(readAll())
    }
    refresh()
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) refresh()
    }
    window.addEventListener("storage", onStorage)
    window.addEventListener(CHANGE_EVENT, refresh)
    return () => {
      window.removeEventListener("storage", onStorage)
      window.removeEventListener(CHANGE_EVENT, refresh)
    }
  }, [])

  return set
}
